import React, {useState} from 'react'
import Breadcrumb, {BreadcrumbType} from '../../components/Breadcrumb'
import {
  AssetsBox,
  AssetTitle,
  IconsBox,
  LeftBox,
  RightBox,
  TimeBox,
} from '../VideoPage/styles'
import AssetsSidenav from '../../components/AssetsSidenav'
import ProvenProcess from '../../../images/icons/new-dashboard/proven-process-icon.svg'
import {getDivisionDetails} from '../../../constants/divisions'
import {
  AssetDescription,
  ExamBox,
  ExamCard,
  ExamCardAttempts,
  ExamCardButtonBox,
  ExamCardtitle,
  ExamListBox,
} from './styles'
import {StudyGuide} from '../../../redux/studyGuides'
import {Helmet} from 'react-helmet'
import {AttemptInProgress, Exam} from '../../../redux/exams'
import hoursMinutes from '../../../utils/hoursMinutes'
import ClockIcon from '../../../images/icons/new-dashboard/clock-icon.svg'
import Loading from '../../../components/Loading'
import {Button} from '../../../components/button'
import ExamsIcon from '../../../images/icons/new-dashboard/exam-icon.svg'
import {Link} from 'react-router-dom'
import {
  ContinueExamBox,
  InProgressTitle,
  LoggedOutBox,
} from '../../../pages/ExamsList/styles'
import Countdown from 'react-countdown'
import {useAuth} from '../../../components/AuthProvider'
import {useAppDispatch} from '../../../redux/configureStore'
import {useSelector} from 'react-redux'
import {selectMember, selectMemberAccess} from '../../../redux/members'
import {Box} from '@mui/material'
import LogInButton from '../../../components/LogInButton/logInButton'
import LockIcon from '@mui/icons-material/Lock'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {handleUpgrade} from '../../../utils/upgradeHelper'

type Props = {
  studyGuide?: StudyGuide
  studyDay?: string
  examId?: string
  exams: Exam[]
  inProgress?: AttemptInProgress
}

const View = ({studyGuide, studyDay, examId, exams, inProgress}: Props) => {
  const auth = useAuth()
  const dispatch = useAppDispatch()
  const {isMobile} = useWindowDimensions()

  const [isExpanded, setIsExpanded] = useState(isMobile ? false : true)

  const access = useSelector(selectMemberAccess)
  const member = useSelector(selectMember)

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.exams?.value) === 1
      : false

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: studyGuide?.courseTitle ?? '',
      url: `/study-guide/${studyGuide?.id}`,
    },
    {
      title: `Day ${studyDay}`,
      url: `/study-guide/${studyGuide?.id}`,
    },
  ]

  const goToExam = (examId: string) => {
    window.open(`/practice-exams/questions/${examId}`)
  }

  const division = getDivisionDetails(studyGuide?.abbreviation ?? null)

  const exam = exams[0]
  const title = exam?.title.split('-')

  const currentExamId = inProgress?.attemptId
  if (!currentExamId) localStorage.clear()

  const isExamRunning = currentExamId === exam?.id

  const renderer = ({total, hours, minutes, seconds, completed}: any) => {
    if (completed) {
      localStorage.clear()
    } else {
      localStorage.setItem('timerProgress', String(total / 1000))
      return (
        <Link to={`/practice-exams/questions/${currentExamId}`} target="_blank">
          Continue exam in progress ({String(hours).padStart(2, '0')}:
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}{' '}
          remaining)
        </Link>
      )
    }
  }

  return (
    <>
      <Helmet>
        <title>{`Practice Exam | Black Spectacles`}</title>
      </Helmet>
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <AssetsBox>
        <LeftBox $isExpanded={isExpanded}>
          <AssetsSidenav
            currentAssetId={examId}
            studyGuide={studyGuide}
            studyDay={Number(studyDay)}
            isExpanded={isExpanded}
            onChangeIsExpanded={(value: boolean) => setIsExpanded(value)}
          />
        </LeftBox>
        <RightBox>
          {!exam ? (
            <Loading />
          ) : (
            <>
              <IconsBox>
                <img src={division?.icon} alt={division?.name} />
                <img src={ProvenProcess} alt="Proven Process" />
              </IconsBox>
              <AssetTitle>Practice Exam</AssetTitle>
              <AssetDescription>
                This practice exam form includes two case studies and a set of
                unique questions designed to test your knowledge—not just your
                memory. Each time you take this form, the questions will be
                shuffled, and your scores will be saved for review.
                <br />
                <br />A new form will be available each day, allowing you to
                focus on different question sets over time.
              </AssetDescription>
              <ExamBox>
                {currentExamId && (
                  <ContinueExamBox>
                    <InProgressTitle>In-progress exam:</InProgressTitle>
                    <Countdown
                      date={
                        Date.now() +
                        (localStorage.getItem('timerProgress')
                          ? parseInt(localStorage.getItem('timerProgress')!) *
                            1000
                          : 0)
                      }
                      renderer={renderer}
                    />
                  </ContinueExamBox>
                )}
                <TimeBox>
                  <img src={ClockIcon} alt="Duration" />{' '}
                  {hoursMinutes((exam.examDuration ?? 0) * 60)}
                </TimeBox>
                <TimeBox>{exam.questionCount} questions</TimeBox>
                <ExamListBox>
                  {!auth.isAuthenticated ||
                    (!hasAccess && (
                      <LoggedOutBox>
                        <Box>
                          You have reached one of our{' '}
                          <a
                            href="https://www.blackspectacles.com/blog/black-spectacles-becomes-ncarbs-first-ever-approved-test-prep-provider-1"
                            target="_blank"
                          >
                            NCARB-approved
                          </a>{' '}
                          Premium Practice Exams.
                        </Box>
                        <Box>
                          {!auth.isAuthenticated ? (
                            <>
                              <LogInButton />
                              to access this material.
                            </>
                          ) : (
                            <>
                              <Link
                                to={'#'}
                                onClick={handleUpgrade(
                                  dispatch,
                                  auth,
                                  member,
                                  'practice exams paywall',
                                )}
                              >
                                <LockIcon /> Upgrade your membership
                              </Link>{' '}
                              to access this material.
                            </>
                          )}
                        </Box>
                      </LoggedOutBox>
                    ))}
                  {hasAccess && (
                    <ExamCard>
                      <ExamCardtitle>
                        <img src={ExamsIcon} alt="Exam" />
                        {title[1]}
                      </ExamCardtitle>
                      <ExamCardButtonBox>
                        <Button
                          children={
                            !currentExamId
                              ? 'Start Exam'
                              : isExamRunning
                              ? 'Continue Exam'
                              : 'Exam in Progress'
                          }
                          color={
                            !currentExamId || isExamRunning
                              ? 'primary'
                              : 'whiteDisabled'
                          }
                          onClick={() =>
                            !currentExamId || isExamRunning
                              ? goToExam(exam.id)
                              : {}
                          }
                        />
                      </ExamCardButtonBox>
                      <ExamCardAttempts>
                        {exam.attempts && exam.attempts?.length > 0 ? (
                          <>
                            {exam.attempts.map((attempt, key) => {
                              const dateObject = new Date(attempt.date)
                              const formattedDate =
                                dateObject.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })

                              return (
                                <Link
                                  to={`/practice-exams/review/${attempt.attemptId}`}
                                  target="_blank"
                                  key={`att-${key}`}
                                >
                                  {formattedDate} - Score:{' '}
                                  {attempt.correctAnswers}/{exam.questionCount}{' '}
                                  ({Math.floor(attempt.percentage)}%)
                                </Link>
                              )
                            })}
                          </>
                        ) : (
                          <>
                            Practice exams will be reviewable after they are
                            completed.
                          </>
                        )}
                      </ExamCardAttempts>
                    </ExamCard>
                  )}
                </ExamListBox>
              </ExamBox>
            </>
          )}
        </RightBox>
      </AssetsBox>
    </>
  )
}

export default View
